import { getHeaders } from './localStorage';
import axios from 'axios';
import { api, versao } from '../config';
import errorHandling from './errorHandling';
import { GET_TEMPLATES, GET_CLIENTS_AUDIENCE, GET_LEADS_AUDIENCE, GET_AUDIENCES, GET_CAMPAIGNS } from './types';

// Cache for API responses
const apiCache = {
	templates: {},
	campaigns: {},
	audiences: {},
	clients: {},
	leads: {}
};

// Cache timeout in milliseconds (5 minutes)
const CACHE_TIMEOUT = 5 * 60 * 1000;

export const listTemplates = (loja, cb, page = 1, limit = 10) => {
	return function(dispatch){
		const cacheKey = `${loja}_${page}_${limit}`;
		const cachedData = apiCache.templates[cacheKey];
		const now = new Date().getTime();
		
		if (cachedData && (now - cachedData.timestamp < CACHE_TIMEOUT)) {
			dispatch({ type: GET_TEMPLATES, payload: cachedData.data });
			if (cb) return cb(cachedData.data);
			return;
		}
		
		axios.get(`${api}/${versao}/api/email-marketing/templates?loja=${loja}&page=${page}&limit=${limit}`, getHeaders())
		.then(response => {
			apiCache.templates[cacheKey] = {
				data: response.data,
				timestamp: now
			};
			
			dispatch({ type: GET_TEMPLATES, payload: response.data });
			if (cb) return cb(response.data);
		})
		.catch(error => {
			errorHandling(error);
			if (cb) cb(null);
		});
	}
}

export const saveTemplate = ({ _id, title, design, html }, loja, cb) => {
	return function(){
		// Invalidate cached templates so UI refresh shows the updated list
		Object.keys(apiCache.templates)
			.filter(k => k.startsWith(`${loja}_`))
			.forEach(k => delete apiCache.templates[k]);

		axios.put(`${api}/${versao}/api/email-marketing/templates/save?loja=${loja}`, { _id, title, design, html }, getHeaders())
		.then(response => cb(response.data.template))
		.catch(errorHandling);
	}
}

export const removeTemplate = (templateId, loja, cb) => {
	return function(){
		axios.delete(`${api}/${versao}/api/email-marketing/templates/${templateId}?loja=${loja}`, getHeaders())
		.then(response => {
			// Invalidate cached templates so UI refresh doesn't show deleted items
			Object.keys(apiCache.templates)
				.filter(k => k.startsWith(`${loja}_`))
				.forEach(k => delete apiCache.templates[k]);

			return cb({ success: true })
		})
		.catch(error => {
			return cb({ success: false })
		})
	}
}

export const listClientsAudience = (loja, cb) => {
	return function(dispatch){
		axios.get(`${api}/${versao}/api/clientes/all?loja=${loja}`, getHeaders())
		.then(async response => {
			var clients = [];
			await response.data?.clientes?.forEach(client => {
				if(!client?.email || clients?.filter(_client => _client.email === client?.email)[0]) return null;
				clients.push(client)
			})

			dispatch({ type: GET_CLIENTS_AUDIENCE, payload: { clientes: clients } })
			return cb(response.data)
		})
		.catch(errorHandling);
	}
}

export const listLeadsAudience = (loja, cb) => {
	return function(dispatch){
		axios.get(`${api}/${versao}/api/leads/all?loja=${loja}`, getHeaders())
		.then(response => {
			dispatch({ type: GET_LEADS_AUDIENCE, payload: response.data })
			return cb(response.data)
		})
		.catch(errorHandling);
	}
}

export const saveAudience = ({ _id, title, leads, leadsType }, loja, cb) => {
	return function(){
		Object.keys(apiCache.audiences)
			.filter(k => k.startsWith(loja))
			.forEach(k => delete apiCache.audiences[k]);

		axios.put(`${api}/${versao}/api/email-marketing/audiences/save?loja=${loja}`, { _id, title, leads, leadsType }, getHeaders())
		.then(response => cb(response.data.audience))
		.catch(errorHandling);
	}
}

export const listAudiences = (loja, cb) => {
	return function(dispatch){
		const cachedData = apiCache.audiences[loja];
		const now = new Date().getTime();
		
		if (cachedData && (now - cachedData.timestamp < CACHE_TIMEOUT)) {
			dispatch({ type: GET_AUDIENCES, payload: cachedData.data });
			if (cb) return cb(cachedData.data);
			return;
		}
		
		axios.get(`${api}/${versao}/api/email-marketing/audiences?loja=${loja}`, getHeaders())
		.then(response => {
			apiCache.audiences[loja] = {
				data: response.data,
				timestamp: now
			};
			
			dispatch({ type: GET_AUDIENCES, payload: response.data });
			if (cb) return cb(response.data);
		})
		.catch(error => {
			errorHandling(error);
			if (cb) cb(null);
		});
	}
}

export const removeAudience = (_id, loja, cb) => {
	return function(){
		axios.delete(`${api}/${versao}/api/email-marketing/audience/${_id}?loja=${loja}`, getHeaders())
		.then(response => {
			Object.keys(apiCache.audiences)
				.filter(k => k.startsWith(loja))
				.forEach(k => delete apiCache.audiences[k]);

			return cb({ success: true })
		})
		.catch(error => {
			return cb({ success: false })
		})
	}
}

export const createCampaign = (data, loja, callback, errorCallback) => {
	return function(dispatch){
		// Show that we're starting the request
		console.log("Starting campaign creation...");
		
		axios.post(`${api}/${versao}/api/email-marketing/campaigns?loja=${loja}`, data, getHeaders())
		.then(response => {
			console.log("Campaign created successfully");
			
			Object.keys(apiCache.campaigns).filter(k => k.startsWith(loja)).forEach(k => delete apiCache.campaigns[k]);
			
			if(callback) callback(response.data);
		})
		.catch(error => {
			console.error("Campaign creation error:", error);
			if(errorCallback) errorCallback(error);
			else errorHandling(error);
		});
	}
}

export const listCampaigns = (loja, cb, page = 1, limit = 10) => {
	return function(dispatch){
		const cacheKey = `${loja}_${page}_${limit}`;
		const cachedData = apiCache.campaigns[cacheKey];
		const now = new Date().getTime();
		
		if (cachedData && (now - cachedData.timestamp < CACHE_TIMEOUT)) {
			dispatch({ type: GET_CAMPAIGNS, payload: cachedData.data });
			if (cb) return cb(cachedData.data);
			return;
		}
		
		axios.get(`${api}/${versao}/api/email-marketing/campaigns?loja=${loja}&page=${page}&limit=${limit}`, getHeaders())
		.then(response => {
			apiCache.campaigns[cacheKey] = {
				data: response.data,
				timestamp: now
			};
			
			dispatch({ type: GET_CAMPAIGNS, payload: response.data });
			if (cb) return cb(response.data);
		})
		.catch(error => {
			errorHandling(error);
			if (cb) cb(null);
		});
	}
}

export const deleteCampaignsOlderThanOneYear = (loja, cb) => {
	return function(dispatch){
		axios.delete(`${api}/${versao}/api/email-marketing/campaigns/old?loja=${loja}`, getHeaders())
		.then(response => {
			Object.keys(apiCache.campaigns).filter(k => k.startsWith(loja)).forEach(k => delete apiCache.campaigns[k]);
			if (cb) cb(response.data);
		})
		.catch(error => {
			errorHandling(error);
			if (cb) cb(null);
		});
	}
}